import React, { useState } from 'react';
import { Outlet, Link, useLocation, Navigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Logo } from '../sv';
import {
  LayoutDashboard, Package, FileText, MapPin, CreditCard, Settings, Users, UserCheck, UserPlus, Container, Anchor, Globe,
  DollarSign, Mail, Inbox, BarChart3, LogOut, Menu, X, Loader2, Mountain, Bell, Search, ArrowLeft
} from 'lucide-react';

const MENUS = {
  user: [
    { section: 'Shipments', items: [
      { name: 'Overview', href: '/dashboard', icon: LayoutDashboard },
      { name: 'Bookings', href: '/dashboard/bookings', icon: Package },
      { name: 'Quotes', href: '/dashboard/quotes', icon: FileText },
      { name: 'Tracking', href: '/dashboard/tracking', icon: MapPin } ] },
    { section: 'Book', items: [
      { name: 'Book a container', href: '/booking', icon: Container },
      { name: 'Book bulk cargo', href: '/booking?mode=bulk', icon: Mountain } ] },
    { section: 'Account', items: [
      { name: 'Documents', href: '/dashboard/documents', icon: FileText },
      { name: 'Payments', href: '/dashboard/payments', icon: CreditCard },
      { name: 'Settings', href: '/dashboard/settings', icon: Settings } ] },
  ],
  agent: [
    { section: 'Operations', items: [
      { name: 'Dashboard', href: '/agent', icon: LayoutDashboard },
      { name: 'Bookings', href: '/agent/bookings', icon: Package },
      { name: 'Walk-in booking', href: '/agent/walkin-booking', icon: UserPlus },
      { name: 'Containers', href: '/agent/containers', icon: Container },
      { name: 'Documents', href: '/agent/documents', icon: FileText } ] },
    { section: 'Customers', items: [
      { name: 'Pending approvals', href: '/agent/approvals', icon: UserCheck },
      { name: 'Customers', href: '/agent/customers', icon: Users },
      { name: 'Quotes', href: '/agent/quotes', icon: FileText },
      { name: 'Reports', href: '/agent/reports', icon: BarChart3 } ] },
  ],
  admin: [
    { section: 'Operations', items: [
      { name: 'Dashboard', href: '/admin', icon: LayoutDashboard },
      { name: 'Bookings', href: '/admin/bookings', icon: Package },
      { name: 'Walk-in booking', href: '/admin/walkin-booking', icon: UserPlus },
      { name: 'Containers', href: '/admin/containers', icon: Container },
      { name: 'Documents', href: '/admin/documents', icon: FileText } ] },
    { section: 'People', items: [
      { name: 'Pending approvals', href: '/admin/approvals', icon: UserCheck },
      { name: 'Users', href: '/admin/users', icon: Users },
      { name: 'Agents', href: '/admin/agents', icon: UserPlus },
      { name: 'Messages', href: '/admin/messages', icon: Inbox } ] },
    { section: 'Network', items: [
      { name: 'Fleet', href: '/admin/fleet', icon: Anchor },
      { name: 'Ports', href: '/admin/ports', icon: Globe },
      { name: 'Pricing', href: '/admin/pricing', icon: DollarSign },
      { name: 'Analytics', href: '/admin/analytics', icon: BarChart3 },
      { name: 'Notifications', href: '/admin/notifications', icon: Mail },
      { name: 'Settings', href: '/admin/settings', icon: Settings } ] },
  ],
};

function DashboardLayout({ type = 'user' }) {
  const { user, logout, isAuthenticated, isAdmin, isAgent, loading } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-gray-50" data-testid="dashboard-auth-loading"><Loader2 className="h-6 w-6 animate-spin text-gray-600" /></div>;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (type === 'admin' && !isAdmin) return <Navigate to="/dashboard" replace />;
  if (type === 'agent' && !isAgent) return <Navigate to="/dashboard" replace />;

  const bridge = type !== 'user';
  const product = bridge ? 'Bridge' : 'Compass';
  const baseRoute = type === 'admin' ? '/admin' : type === 'agent' ? '/agent' : '/dashboard';
  const sections = MENUS[type];

  function isActive(href) {
    const [path, qs] = href.split('?');
    if (path === baseRoute) return location.pathname === baseRoute;
    if (qs) return location.pathname === path && location.search.includes(qs);
    if (path === '/booking') return location.pathname === path && !location.search.includes('mode=bulk');
    return location.pathname.startsWith(path);
  }

  const sideCls = bridge ? 'bg-ink text-white border-r border-white/10' : 'bg-white text-ink border-r border-gray-200';
  const linkBase = 'flex items-center gap-3 h-10 px-3 rounded text-[15px] transition-colors duration-[120ms]';
  const linkIdle = bridge ? 'text-white/80 hover:bg-white/10 hover:text-white' : 'text-ink hover:bg-gray-50';
  const linkActive = bridge ? 'bg-white/[0.12] text-white font-medium' : 'bg-blue-100 text-ink font-medium';

  function Sidebar({ mobile = false }) {
    return (
      <div className={"flex flex-col h-full w-[240px] " + sideCls} data-testid="dashboard-sidebar">
        <div className={"h-16 flex items-center px-5 border-b " + (bridge ? 'border-white/10' : 'border-gray-200')}>
          <Link to="/"><Logo inverse={bridge} size={18} product={product} /></Link>
        </div>
        <nav className="flex-1 overflow-y-auto py-3 px-3">
          {sections.map(function (sec) {
            return (
              <div key={sec.section} className="mb-4">
                <div className={"px-3 py-2 text-[12px] " + (bridge ? 'text-white/50' : 'text-gray-600')}>{sec.section}</div>
                <div className="grid gap-0.5">
                  {sec.items.map(function (it) {
                    return (
                      <Link key={it.href} to={it.href} onClick={function () { if (mobile) setOpen(false); }} data-testid={"sidebar-" + it.name.toLowerCase().replace(/\s+/g, '-')}
                        className={linkBase + ' ' + (isActive(it.href) ? linkActive : linkIdle)}>
                        <it.icon className="h-[18px] w-[18px] shrink-0" strokeWidth={1.75} />{it.name}
                      </Link>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </nav>
        <div className={"p-3 border-t " + (bridge ? 'border-white/10' : 'border-gray-200')}>
          <div className="flex items-center gap-3 px-2 py-2">
            <div className={"w-8 h-8 rounded-full grid place-items-center text-[12px] font-medium " + (bridge ? 'bg-white/15 text-white' : 'bg-blue-100 text-ink')}>{user?.first_name?.[0]}{user?.last_name?.[0]}</div>
            <div className="flex-1 min-w-0"><p className="text-sm truncate">{user?.first_name} {user?.last_name}</p><p className={"text-[12px] capitalize " + (bridge ? 'text-white/60' : 'text-gray-600')}>{user?.role}{user?.company ? ' · ' + user.company : ''}</p></div>
          </div>
          <button onClick={logout} className={linkBase + ' w-full ' + linkIdle} data-testid="sidebar-logout"><LogOut className="h-[18px] w-[18px]" strokeWidth={1.75} />Log out</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid={"dashboard-layout-" + type}>
      <div className="lg:hidden sticky top-0 z-40 h-14 bg-white border-b border-gray-200 flex items-center justify-between px-4">
        <Link to="/"><Logo size={16} product={product} /></Link>
        <button className="p-2 rounded hover:bg-gray-100" onClick={function () { setOpen(!open); }} aria-label="Toggle menu" data-testid="dashboard-mobile-menu">{open ? <X className="h-5 w-5" strokeWidth={1.75} /> : <Menu className="h-5 w-5" strokeWidth={1.75} />}</button>
      </div>
      {open && (
        <div className="lg:hidden fixed inset-0 z-30 bg-ink/50" onClick={function () { setOpen(false); }}>
          <div className="absolute left-0 top-14 bottom-0" onClick={function (e) { e.stopPropagation(); }}><Sidebar mobile /></div>
        </div>
      )}
      <div className="flex">
        <div className="hidden lg:block fixed left-0 top-0 bottom-0 z-30"><Sidebar /></div>
        <main className="flex-1 lg:ml-[240px] min-w-0">
          <div className="hidden lg:flex h-16 items-center justify-between px-8 bg-white border-b border-gray-200 sticky top-0 z-20" data-testid="dashboard-topbar">
            <div className="flex items-center gap-3 text-[15px]">
              <Link to="/" className="text-gray-600 hover:text-ink inline-flex items-center gap-1.5"><ArrowLeft className="h-4 w-4" strokeWidth={1.75} />Website</Link>
              <span className="text-gray-400">·</span>
              <span className="text-gray-600">{product}</span>
            </div>
            <div className="flex items-center gap-2">
              {bridge && <div className="hidden xl:flex items-center gap-2 h-9 px-3 rounded border border-gray-300 text-gray-600 text-sm w-[260px]"><Search className="h-4 w-4" strokeWidth={1.75} /><span className="flex-1">Search bookings, customers</span><span className="sv-mono text-[12px]">⌘K</span></div>}
              <button className="h-9 w-9 rounded grid place-items-center hover:bg-gray-100 text-gray-700" aria-label="Notifications"><Bell className="h-[18px] w-[18px]" strokeWidth={1.75} /></button>
              <Link to={baseRoute + (type === 'user' ? '/settings' : type === 'admin' ? '/settings' : '')} className="h-9 px-3 rounded hover:bg-gray-100 text-sm inline-flex items-center gap-2"><span className="w-7 h-7 rounded-full bg-blue-100 grid place-items-center text-[12px] font-medium">{user?.first_name?.[0]}{user?.last_name?.[0]}</span>{user?.first_name}</Link>
            </div>
          </div>
          <div className="p-5 md:p-8 max-w-[1400px]">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}

export default DashboardLayout;
